/**
 * Environment Configuration Loader
 * Centralizes all environment variable access with validation and fallbacks
 */

const ENV = import.meta.env?.VITE_ENV || 'development';
const IS_DEV = ENV === 'development';
const IS_STAGING = ENV === 'staging';
const IS_PROD = ENV === 'production';

/**
 * Get environment variable with validation
 * @param {string} key - Environment variable key (without VITE_ prefix)
 * @param {string} fallback - Fallback value for development
 * @param {boolean} isCritical - Whether this variable is required for production
 * @returns {string} Environment variable value
 */
const getEnvVar = (key, fallback = '', isCritical = false) => {
  const value = import.meta.env?.[`VITE_${key}`];
  
  // In production, throw error if required critical env var is missing
  if (IS_PROD && !value && isCritical) {
    throw new Error(
      `🚨 CRITICAL: Missing required environment variable: VITE_${key}\n` +
      `This variable is required for production deployment.\n` +
      `Please configure it in your hosting platform's environment settings.`
    );
  }
  
  // Use value if available, otherwise use fallback silently
  return value || fallback;
};

/**
 * Application Configuration
 */
export const config = {
  // Environment
  env: ENV,
  isDev: IS_DEV,
  isStaging: IS_STAGING,
  isProd: IS_PROD,
  
  // Application Identity (optional, using fallbacks)
  appId: getEnvVar('APP_ID', 'Mervo'),
  appTag: getEnvVar('APP_TAG', 'mervo'),
  
  // Supabase Configuration (critical for production)
  supabase: {
    url: getEnvVar('SUPABASE_URL', 'https://placeholder.supabase.co', true),
    anonKey: getEnvVar('SUPABASE_ANON_KEY', 'placeholder-anon-key', true),
  },
  
  // Feature Flags
  features: {
    enableServiceWorker: IS_PROD,
    enableDebugLogs: IS_DEV,
    enableQAPanel: IS_DEV || IS_STAGING,
  },
};

/**
 * Validate all required environment variables
 * Call this at app initialization
 */
export const validateEnvironment = () => {
  const errors = [];
  
  if (IS_PROD) {
    // Check all required production variables (only critical ones)
    const required = [
      'SUPABASE_URL',
      'SUPABASE_ANON_KEY',
    ];
    
    required?.forEach(key => {
      if (!import.meta.env?.[`VITE_${key}`]) {
        errors?.push(`Missing VITE_${key}`);
      }
    });
    
    if (errors?.length > 0) {
      throw new Error(
        `🚨 PRODUCTION DEPLOYMENT BLOCKED:\n` +
        errors.map(err => `  - ${err}`).join('\n') +
        `\n\nConfigure these variables in your hosting platform before deploying.`
      );
    }
  }
  
  console.log(`✅ Environment validated: ${ENV}`);
  return true;
};

/**
 * Log configuration (only in development, with clean output)
 */
if (IS_DEV) {
  console.log('🔧 Mervo Environment Ready:', {
    env: ENV,
    supabaseConnected: config?.supabase?.url !== 'https://placeholder.supabase.co',
  });
}

export default config;